/**
 * The **Arena kill-streak combo machine** (PRD #432 Slice A): a pure,
 * clock-injected state machine that counts enemy kills inside a rolling window
 * and escalates through tiers. It is the third leg of the arena feedback push
 * (#430 hit-feel, #431 collection-jackpot, this one *sustained output*), and
 * like its siblings it is derived entirely from what the client already knows -
 * enemy `die` clips - with **zero** sim/wire/protocol changes, so a combo can
 * never desync, be cheated for reward, or cost a balance pass.
 *
 * The window is {@link ARENA_COMBO_WINDOW_MS}, **re-armed by every kill** (the
 * standard streak model: a chain lives as long as kills stay within the window
 * of each other; no cumulative-time variant), and the counter appears only at
 * {@link ARENA_COMBO_SHOW_AT} so a small chain stays quiet - the number must
 * mean something. Tiers ({@link ARENA_COMBO_TIERS}) escalate the styling and
 * fire a one-shot stinger on each crossing.
 *
 * Pure and clock-injected (the {@link ./render/screenShake.ScreenShake} /
 * {@link ./arenaDanger} discipline): no DOM, no audio, no timers, no
 * randomness - the caller feeds it the clock (`recordKill(atMs)` /
 * `sync(atMs)` / `state(atMs)`) and drives the DOM ({@link ./arenaCombo}) and
 * the tier stingers off the returns. Every window, tier, and expiry curve is
 * node-testable without Pixi or jsdom.
 */

/** How long a chain survives between kills (ms); re-armed by every kill. */
export const ARENA_COMBO_WINDOW_MS = 3000;

/**
 * The tier thresholds: crossing each escalates the styling (`data-tier`) and
 * fires one rising stinger. The count at each boundary is inclusive - a count
 * of 25 is tier 3. The first entry doubles as {@link ARENA_COMBO_SHOW_AT}.
 */
export const ARENA_COMBO_TIERS = [5, 10, 25, 50, 100] as const;

/**
 * The count at which the counter first appears (== the first tier boundary):
 * chains below this stay invisible so the counter reads as "you are on a real
 * streak", not "you killed something".
 */
export const ARENA_COMBO_SHOW_AT = ARENA_COMBO_TIERS[0];

/** The derived state one render frame reads off the machine. */
export interface ComboState {
  /** The live chain length (0 = no chain). */
  count: number;
  /** How many tier thresholds `count` has crossed: 0..{@link ARENA_COMBO_TIERS}.length. */
  tier: number;
  /** The window's remaining life as a fraction [0, 1]; 1 at the last kill, draining to 0 at expiry. */
  windowFraction: number;
  /** Whether the counter should show (`count >= ARENA_COMBO_SHOW_AT`). */
  visible: boolean;
  /** The best (longest) chain reached this run - survives a break, resets only on {@link ComboTracker.reset}. */
  bestThisRun: number;
}

/** The handle `main.ts` drives the combo through. */
export interface ComboTracker {
  /**
   * Record one kill at instant `atMs`: extend (or, if the window already
   * lapsed, start fresh) the chain, re-arm the window, and update the run's
   * best. Returns the tier index (1-based) this kill **newly crossed** for the
   * caller to sting, or `0` if no boundary was crossed. At most one boundary
   * can be crossed by a single +1 kill.
   */
  recordKill(atMs: number): number;
  /**
   * Age the chain to instant `atMs`: if the window has lapsed with a live
   * chain, break it (count -> 0) and return the length that just expired for
   * the fall-away animation. Edge-triggered - it returns the broken length
   * exactly once (the storm-cue discipline), then `0` while idle.
   */
  sync(atMs: number): number;
  /** The derived state at instant `atMs` (never mutates - call after `sync`). */
  state(atMs: number): ComboState;
  /**
   * Pause or resume the chain's expiry clock (the Arena level-up draft freeze,
   * ADR-0176): while paused the window neither drains nor lapses, so a streak
   * survives the time the player spends picking an upgrade - the run itself is
   * frozen server-side, and ticking the combo on the client's wall clock would
   * silently break a chain the player has *not* actually let lapse. Idempotent
   * (a no-op if already in that state); `atMs` is the current render clock. On
   * resume the window's remaining life is preserved exactly - the anchor is
   * shifted forward by the frozen span, so a chain that had 1.2s left when the
   * draft opened still has 1.2s left when it closes.
   */
  setPaused(paused: boolean, atMs: number): void;
  /** Back to a fresh run: chain AND best cleared - the run-start / Welcome guard. */
  reset(): void;
}

/**
 * The pure tier of a chain length: how many of {@link ARENA_COMBO_TIERS} the
 * count has reached. `0` below the first boundary, up to the array's length at
 * the top tier. Exported so the DOM and tests share the one classification.
 */
export function comboTier(count: number): number {
  let tier = 0;
  for (const threshold of ARENA_COMBO_TIERS) {
    if (count >= threshold) tier += 1;
  }
  return tier;
}

/** Wire a fresh combo machine (one per client; reset on every Welcome / run start). */
export function createComboTracker(): ComboTracker {
  /** The live chain length; 0 = no chain. */
  let count = 0;
  /** When the last kill landed - the window's anchor. */
  let lastKillAt = 0;
  /** The longest chain this run, kept across breaks until `reset()`. */
  let best = 0;
  /** Whether the expiry clock is frozen for a level-up draft (see `setPaused`). */
  let paused = false;
  /** The instant the pause began - the frozen clock's fixed value while paused. */
  let pausedAt = 0;

  /**
   * The effective clock: frozen at `pausedAt` while paused so the window neither
   * drains nor lapses, otherwise the live `atMs`. Every time-reading path routes
   * through this so a pause is honoured uniformly (lapse, drain, kill-anchor).
   */
  const effNow = (atMs: number): number => (paused ? pausedAt : atMs);

  /** Whether the window has lapsed for a live chain (a break is due / overdue). */
  const lapsed = (atMs: number): boolean =>
    count > 0 && effNow(atMs) >= lastKillAt + ARENA_COMBO_WINDOW_MS;

  return {
    recordKill(atMs) {
      // A kill after the window lapsed starts a brand-new chain (defensive:
      // sync() normally breaks it first, but a kill arriving on a hidden tab -
      // where the render loop's sync is paused - must not extend a dead chain).
      const prevTier = lapsed(atMs) ? 0 : comboTier(count);
      count = lapsed(atMs) ? 1 : count + 1;
      lastKillAt = effNow(atMs);
      if (count > best) best = count;
      const newTier = comboTier(count);
      return newTier > prevTier ? newTier : 0;
    },
    sync(atMs) {
      if (!lapsed(atMs)) return 0;
      const broken = count;
      count = 0;
      return broken;
    },
    setPaused(next, atMs) {
      if (next === paused) return;
      if (next) {
        // Freeze the clock here: the window stops draining/lapsing from now.
        pausedAt = atMs;
      } else {
        // Resume: slide the anchor forward by the frozen span so the chain's
        // remaining life is exactly what it was when the draft opened.
        lastKillAt += atMs - pausedAt;
      }
      paused = next;
    },
    state(atMs) {
      const now = effNow(atMs);
      const windowFraction =
        count > 0
          ? Math.min(1, Math.max(0, (lastKillAt + ARENA_COMBO_WINDOW_MS - now) / ARENA_COMBO_WINDOW_MS))
          : 0;
      return {
        count,
        tier: comboTier(count),
        windowFraction,
        visible: count >= ARENA_COMBO_SHOW_AT,
        bestThisRun: best,
      };
    },
    reset() {
      count = 0;
      lastKillAt = 0;
      best = 0;
      paused = false;
      pausedAt = 0;
    },
  };
}
